import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { View, ScrollView, Text, StyleSheet, ActivityIndicator, Dimensions, Image, TouchableOpacity, Platform } from 'react-native';
import { useCardSEO } from '../utils/seo';
import ModuleHeader from '../components/common/ModuleHeader';
import RestaurantEditorView from '@cubemeet/ui/components/cards/RestaurantEditorView';
import LodgingEditorView from '@cubemeet/ui/components/cards/LodgingEditorView';
import PoiEditorView from '@cubemeet/ui/components/cards/PoiEditorView';
import ActivityEditorView from '@cubemeet/ui/components/cards/ActivityEditorView';
import ShoppingEditorView from '@cubemeet/ui/components/cards/ShoppingEditorView';
import CardDetailContent from '@cubemeet/ui/components/cards/CardDetailContent';
import { supabase as supabaseClient } from '@cubemeet/core/services/supabase';
import { ShareButton, MapSelectionModal } from '@cubemeet/ui';
import { useGlobalToast } from '../contexts/ToastContext';
import FullscreenWrapper from '@cubemeet/ui/components/common/FullscreenWrapper';
import PlatformIcon from '@cubemeet/ui/components/common/PlatformIcon';
import AddToItineraryModal from '../components/AddToItineraryModal';
import { ensureMetaPlaceholders } from '@cubemeet/ui/components/travel/placeholder-config';
import BookingPlatformsRow from '@cubemeet/ui/components/cards/shared/BookingPlatformsRow';
import useShowBackButton from '../shared/hooks/useShowBackButton';
import useLockBodyScroll from '../hooks/useLockBodyScroll';
import { CardImageGallery } from '@cubemeet/ui/components/cards/CardImageGallery';
import { FaGlobe, FaInstagram, FaFacebook, FaTwitter, FaYoutube, FaTripadvisor, FaTelegram, FaWhatsapp, FaPinterest, FaLinkedin, FaReddit, FaWeixin, FaLine, FaWeibo, FaWikipediaW } from 'react-icons/fa';
import { SiTiktok, SiWechat, SiLine, SiXiaohongshu, SiBilibili, SiWikidata } from 'react-icons/si';
import NearbyMap, { NearbySpot } from '../components/NearbyMap';
import OpeningHoursRow from '@cubemeet/ui/components/travel/OpeningHoursRow';
import { BOTTOM_NAV_GAP, BOTTOM_SPACER_HEIGHT } from '../styles/layout';
import { useTravelI18n } from '../modules/travel/useTravelI18n';
import { checkIsUserLiked, likeUser, unlikeUser } from '@cubemeet/core/services/userProfileService';
import { usePoiEditing } from '@cubemeet/ui/components/travel/hooks/usePoiEditing';
import PoiTitleAddressSection from '@cubemeet/ui/components/travel/PoiTitleAddressSection';
import DetailsTab from '@cubemeet/ui/components/travel/tabs/DetailsTab';
import DescriptionTab from '@cubemeet/ui/components/travel/tabs/DescriptionTab';
import CardTypeSelector from '@cubemeet/ui/components/travel/CardTypeSelector';

export interface CardDetailPageProps {
  /**
   * 是否以 Modal 模式打开 (从列表页点击卡片打开)
   * 如果为 true，会显示在 Modal 中而非独立页面
   */
  isModal?: boolean;
  /** Modal 模式下的关闭回调 */
  onClose?: () => void;
}

/**
 * 卡片详情页面
 * 支持两种模式:
 * 1. 独立页面模式: 通过 URL /card/:id 直接访问，完整页面 + SEO 配置
 * 2. Modal 模式: 从列表页面打开，显示在 Modal 中
 */

// Helper function to parse HTML attribution string
const parseAttribution = (htmlString: string): { text: string; url?: string } => {
  try {
    // Try to parse <a href="url">text</a>
    const linkMatch = htmlString.match(/<a\s+href=["']([^"']+)["'][^>]*>([^<]+)<\/a>/i);
    if (linkMatch) {
      return { text: linkMatch[2], url: linkMatch[1] };
    }
    // Fallback: strip all HTML tags
    const textOnly = htmlString.replace(/<[^>]+>/g, '');
    return { text: textOnly };
  } catch (e) {
    return { text: htmlString };
  }
};

// Helper function to pick social media icon based on URL
const pickSocialIcon = (url: string) => {
  try {
    const u = new URL(url);
    const host = u.hostname.replace(/^www\./, '').toLowerCase();
    const match = (substr: string) => host.includes(substr);
    if (match('instagram.com')) return { Icon: FaInstagram, color: '#E4405F' };
    if (match('facebook.com')) return { Icon: FaFacebook, color: '#1877F2' };
    if (match('twitter.com') || match('x.com')) return { Icon: FaTwitter, color: '#1DA1F2' };
    if (match('youtube.com') || match('youtu.be')) return { Icon: FaYoutube, color: '#FF0000' };
    if (match('tripadvisor.')) return { Icon: FaTripadvisor, color: '#34E0A1' };
    if (match('t.me')) return { Icon: FaTelegram, color: '#229ED9' };
    if (match('whatsapp.com') || match('wa.me')) return { Icon: FaWhatsapp, color: '#25D366' };
    if (match('pinterest.')) return { Icon: FaPinterest, color: '#E60023' };
    if (match('linkedin.')) return { Icon: FaLinkedin, color: '#0A66C2' };
    if (match('reddit.')) return { Icon: FaReddit, color: '#FF4500' };
    // CN/JP popular platforms
    if (match('tiktok.com') || match('douyin.com') || match('iesdouyin.com')) return { Icon: SiTiktok, color: '#000000' };
    if (match('weixin.qq.com') || match('wechat.com') || match('mp.weixin.qq.com')) return { Icon: SiWechat || FaWeixin, color: '#07C160' };
    if (match('line.me') || match('line.com')) return { Icon: SiLine || FaLine, color: '#06C755' };
    if (match('xiaohongshu.com') || match('xhslink.com')) return { Icon: SiXiaohongshu, color: '#FF2442' };
    if (match('bilibili.com') || match('b23.tv')) return { Icon: SiBilibili, color: '#00A1D6' };
    if (match('weibo.com')) return { Icon: FaWeibo, color: '#E6162D' };
  } catch (e) {}
  return { Icon: FaGlobe, color: '#0B61D6' };
};

// 通用标签页组件（适用于所有卡片类型）
const CardWithTabs: React.FC<{
  cardData: any;
  cardType: 'poi' | 'restaurant' | 'lodging' | 'activity' | 'shopping' | 'video' | 'osm';
  buildPoiMeta: (card: any) => Array<{ label: string; value: string }> | undefined;
  supabaseClient: any;
  onOpenMapModal: () => void;
  coords?: { lat: number; lng: number } | null;
  nearbySpots: NearbySpot[];
  nearbyLoading: boolean;
  nearbyError: string | null;
  primaryImageUrl?: string;
  onSelectNearby?: (spotId: string) => void;
}> = ({
  cardData,
  cardType,
  buildPoiMeta,
  supabaseClient,
  onOpenMapModal,
  coords,
  nearbySpots,
  nearbyLoading,
  nearbyError,
  primaryImageUrl,
  onSelectNearby,
}) => {
  const { t } = useTravelI18n();
  const [activeTab, setActiveTab] = React.useState<'details' | 'description' | 'booking' | 'nearby'>('booking');

  React.useEffect(() => {
    if (!coords && activeTab === 'nearby') {
      setActiveTab('booking');
    }
  }, [coords, activeTab]);
  const navigate = useNavigate();
  
  // 处理标签点击事件
  const handleTagClick = (tag: string) => {
    // 跳转到 CardListPage 并预设标签过滤
    navigate(`/cards?tags=${encodeURIComponent(tag)}`);
  };
  
  // 处理地区标签点击事件（使用层级过滤）
  const handleLocationTagClick = (type: string, value: string) => {
    if (!value) return;
    
    // 获取完整的 location 数据
    const locSource = cardData?.location || cardData?.base_payload?.location || cardData?.custom_payload?.location;
    if (!locSource || typeof locSource !== 'object') {
      // 如果没有完整的 location 数据，回退到旧的单一参数模式
      const url = `/cards?locationType=${encodeURIComponent(type)}&location=${encodeURIComponent(value)}`;
      navigate(url);
      return;
    }
    
    // 构建层级 URL 参数
    // 根据点击的层级，包含所有上级参数
    const params = new URLSearchParams();
    
    if (locSource.country) {
      params.append('country', locSource.country);
    }
    
    if (type === 'country') {
      // 只过滤国家
    } else if (locSource.province) {
      params.append('province', locSource.province);
      
      if (type === 'province') {
        // 过滤到省级
      } else if (locSource.city) {
        params.append('city', locSource.city);
        
        if (type === 'city') {
          // 过滤到市级
        } else if (type === 'district' && locSource.district) {
          params.append('district', locSource.district);
        }
      }
    }
    
    const url = `/cards?${params.toString()}`;
    navigate(url);
  };
  
  // 使用和原始模态相同的数据获取逻辑
  // 模态使用行程表副本数据，地址从 custom_payload 获取
  const title = cardData.title || cardData.custom_payload?.title || cardData.base_payload?.title || '';
  const description = cardData.description || cardData.custom_payload?.description || cardData.base_payload?.description || '';
  
  // 地址获取逻辑：照抄原始模态的 pick 逻辑
  const pick = (obj: any, keys: string[]) => {
    if (!obj) return undefined;
    for (const k of keys) {
      if (k in obj && typeof obj[k] !== 'undefined' && obj[k] !== null) return obj[k];
    }
    return undefined;
  };

  // 使用和原始模态相同的地址解析逻辑
  const resolvedAddress = pick(cardData, ['address', 'location', 'location_name'])
    || pick(cardData.custom_payload, ['address', 'location', 'location_name'])
    || pick(cardData.base_payload, ['address', 'location', 'location_name', 'placeName'])
    || undefined;

  const address = typeof resolvedAddress === 'string' ? resolvedAddress : 
    (resolvedAddress && typeof resolvedAddress === 'object' ? JSON.stringify(resolvedAddress) : '');

  // Debug: 检查地址数据
  console.log('[POIWithTabs] Address debug:', {
    cardDataAddress: cardData.address,
    customPayloadAddress: cardData.custom_payload?.address,
    basePayloadAddress: cardData.base_payload?.address,
    basePayloadPlaceName: cardData.base_payload?.placeName,
    finalAddress: address,
    cardDataKeys: Object.keys(cardData || {}),
    customPayloadKeys: cardData.custom_payload ? Object.keys(cardData.custom_payload) : null,
    basePayloadKeys: cardData.base_payload ? Object.keys(cardData.base_payload) : null
  });

  // 直接使用真实的 metaPairs 数据，不添加占位符
  const rawMeta = cardData.base_payload?.metaPairs || [];
  
  // 获取 tags 数据
  const tags = cardData.tags || cardData.custom_payload?.tags || cardData.base_payload?.tags || [];
  
  // 过滤掉地址、坐标和网络平台相关的字段（在详细信息标签页中隐藏）
  const isAddressLabel = (lbl?: string) => {
    if (!lbl) return false;
    const s = String(lbl).trim().toLowerCase();
    return ['地址', '地', 'address', 'location'].includes(s);
  };
  
  const isCoordinateLabel = (lbl?: string) => {
    if (!lbl) return false;
    const s = String(lbl).trim().toLowerCase();
    return ['坐标', '座標', 'coordinates', 'coordinate', 'lat', 'lng', 'latitude', 'longitude'].includes(s);
  };
  
  const isWebsiteLabel = (lbl?: string) => {
    if (!lbl) return false;
    const s = String(lbl).trim().toLowerCase();
    const aliases = [
      '网站','網站','网址','網址','官网','官網','官方網站','官方网站',
      'website','web','url','site','homepage','home page','link','链接','連結',
      '网络平台','網絡平台','平台','社交平台'
    ];
    return aliases.some(a => s === String(a).toLowerCase());
  };
  
  // 检查是否为 Wikidata 或 Wikipedia 字段
  const isWikiLabel = (lbl?: string) => {
    if (!lbl) return false;
    const s = String(lbl).trim().toLowerCase();
    return s === 'wikidata' || s === 'wikipedia';
  };
  
  // 只显示有实际值的字段（过滤空值和占位符）
  const hasRealValue = (value: any): boolean => {
    if (value === undefined || value === null) return false;
    const str = String(value).trim();
    if (str === '' || str === '未提供' || str === '待填写' || str === '待填寫') return false;
    return true;
  };
  
  const displayedMeta = rawMeta.filter(p => 
    p && 
    hasRealValue(p.value) &&
    !isAddressLabel(p?.label) && 
    !isCoordinateLabel(p?.label) && 
    !isWebsiteLabel(p?.label) &&
    !isWikiLabel(p?.label)  // 过滤掉 Wiki 字段，单独显示
  );

  // 原始模态的样式定义
  const modalStyles = {
    section: { marginTop: 8, marginBottom: 8 },
    addressRow: { flexDirection: 'row' as const, alignItems: 'center' as const, marginBottom: 12 },
    addressText: { marginLeft: 8, color: '#8E8E93', fontSize: 15 },
    tabsRow: { flexDirection: 'row' as const, alignItems: 'stretch' as const, gap: 8 },
    tabBtn: { paddingVertical: 8, paddingHorizontal: 10, borderRadius: 8 },
    tabBtnActive: { backgroundColor: '#F3F4F6' },
    tabText: { fontSize: 14, color: '#6B7280', fontWeight: '600' as const },
    tabTextActive: { color: '#111827' },
    tabDivider: { height: 1, backgroundColor: '#E5E7EB', marginTop: 8, marginBottom: 12 },
  };

  const tabs = [
    { key: 'booking', label: t('cardDetail.tabs.book') },
    { key: 'details', label: t('cardDetail.tabs.details') },
    ...(coords ? [{ key: 'nearby', label: t('cardDetail.tabs.nearby') }] : []),
    { key: 'description', label: t('cardDetail.tabs.description') },
  ];

  return (
    <>
      {/* Title + Address - 完全照抄原始模态的结构 */}
      <View style={[modalStyles.section, { marginTop: 0 }]}>
        <Text style={{ fontSize: 28, fontWeight: '800', color: '#111827' }}>{title}</Text>
        <View style={{ height: 2, backgroundColor: '#0B61D6', opacity: 0.16, marginTop: 8, marginBottom: 12 }} />
        
        <View style={modalStyles.addressRow}>
          <PlatformIcon name="location_on" size={18} color="#FF7A00" family="MaterialIcons" />
          <TouchableOpacity 
            style={{ flex: 1, minWidth: 0 }} 
            onPress={onOpenMapModal}
            activeOpacity={0.7}
          >
            <Text style={modalStyles.addressText}>{address || ''}</Text>
          </TouchableOpacity>
          <TouchableOpacity 
            style={{ paddingHorizontal: 6, paddingVertical: 4, marginLeft: 6 }}
            onPress={onOpenMapModal}
          >
            <PlatformIcon name="near_me" size={18} color="#FF7A00" family="MaterialIcons" />
          </TouchableOpacity>
        </View>
        {/* Location filter tags (country, province, city, district) */}
        {(() => {
          const locSource = cardData?.location || cardData?.base_payload?.location || cardData?.custom_payload?.location;
          if (!locSource || typeof locSource !== 'object') return null;
          const order = ['country', 'province', 'city', 'district'];
          const parts: Array<{ key: string; value: string }> = [];
          for (const k of order) {
            const v = locSource[k];
            if (v && typeof v === 'string') {
              // avoid duplicates
              if (!parts.find(p => p.value === v)) parts.push({ key: k, value: v });
            }
          }
          if (parts.length === 0) return null;
          return (
            <View style={{ marginTop: 12 }}>
              <View style={{ height: 1, backgroundColor: '#E5E7EB', marginVertical: 12 }} />
              <View style={{ flexDirection: 'row', alignItems: 'center', marginBottom: 12 }}>
                <PlatformIcon name="place" size={20} color="#007AFF" />
                <Text style={{ fontSize: 16, fontWeight: '600', color: '#111827', marginLeft: 8 }}>地区</Text>
              </View>
              <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 8 }}>
                {parts.map((p, i) => (
                  <TouchableOpacity
                    key={`${p.key}-${p.value}-${i}`}
                    style={{
                      backgroundColor: '#F3F4F6',
                      paddingHorizontal: 12,
                      paddingVertical: 8,
                      borderRadius: 6,
                      flexDirection: 'row',
                      alignItems: 'center'
                    }}
                    onPress={() => handleLocationTagClick(p.key, p.value)}
                    activeOpacity={0.7}
                  >
                    <Text style={{ color: '#6B7280', fontSize: 14, fontWeight: '500' }}># {p.value}</Text>
                  </TouchableOpacity>
                ))}
              </View>
            </View>
          );
        })()}
      </View>

      {/* Tabs - 完全照抄原始模态的结构 */}
      <View style={[modalStyles.section, { marginTop: 16 }]}>
        <View style={modalStyles.tabsRow}>
          {tabs.map((tab) => {
            const isActive = activeTab === (tab.key as any);
            return (
              <TouchableOpacity
                key={tab.key}
                style={[modalStyles.tabBtn, isActive && modalStyles.tabBtnActive]}
                onPress={() => setActiveTab(tab.key as any)}
              >
                <Text style={[modalStyles.tabText, isActive && modalStyles.tabTextActive]}>{tab.label}</Text>
              </TouchableOpacity>
            );
          })}
        </View>
        <View style={modalStyles.tabDivider} />
        
        {/* Tab Content - 完全照抄原始模态的结构 */}
        {activeTab === 'details' ? (
          <>
            {displayedMeta && displayedMeta.length > 0 ? (
              displayedMeta.map((item, idx) => {
                const lbl = String(item.label || '').trim().toLowerCase();
                const isOpeningHours = lbl === '营业时间' || lbl === '營業時間' || lbl === 'opening hours' || lbl === 'opening_hours';
                const isStarRating = lbl === '星級' || lbl === 'star rating' || lbl === 'rating' || lbl === 'stars';

                if (isOpeningHours) {
                  return <OpeningHoursRow key={idx} label={item.label} value={item.value} />;
                }

                if (isStarRating) {
                  const val = item.value;
                  const strVal = Array.isArray(val) ? val[0] : String(val);
                  const numVal = parseFloat(strVal);
                  
                  return (
                    <View key={idx} style={{ 
                      flexDirection: 'row', 
                      alignItems: 'center', 
                      paddingVertical: 10,
                      borderBottomWidth: idx < displayedMeta.length - 1 ? 1 : 0,
                      borderBottomColor: '#F3F4F6'
                    }}>
                      <Text style={{ width: '30%', color: '#6B7280', fontSize: 15 }}>{item.label}</Text>
                      <View style={{ width: '70%' }}>
                        {!isNaN(numVal) ? (
                          <View style={{ flexDirection: 'row', alignItems: 'center' }}>
                            <View style={{ flexDirection: 'row' }}>
                              {[1, 2, 3, 4, 5].map(i => {
                                if (numVal >= i) return <PlatformIcon key={i} name="star" size={18} color="#F59E0B" />;
                                if (numVal >= i - 0.5) return <PlatformIcon key={i} name="star-half" size={18} color="#F59E0B" />;
                                return <PlatformIcon key={i} name="star-border" size={18} color="#D1D5DB" />;
                              })}
                            </View>
                          </View>
                        ) : (
                          <Text style={{ fontSize: 15, color: '#111', fontWeight: '700' }}>{strVal}</Text>
                        )}
                      </View>
                    </View>
                  );
                }

                return (
                  <View key={idx} style={{ 
                    flexDirection: 'row', 
                    alignItems: 'flex-start', 
                    paddingVertical: 10,
                    borderBottomWidth: idx < displayedMeta.length - 1 ? 1 : 0,
                    borderBottomColor: '#F3F4F6'
                  }}>
                    <Text style={{ width: '30%', color: '#6B7280', fontSize: 15 }}>{item.label}</Text>
                    <Text style={{ width: '70%', fontSize: 15, color: '#111', fontWeight: '700', textAlign: 'left', whiteSpace: 'pre-wrap' }}>
                      {String(item.value)}
                    </Text>
                  </View>
                );
              })
            ) : (
              <Text style={{ color: '#9CA3AF', fontSize: 14 }}>暂无详细信息</Text>
            )}
            
            {/* Wikidata - 只在有值时显示 */}
            {(() => {
              const wikidataField = rawMeta.find(p => String(p?.label || '').trim().toLowerCase() === 'wikidata');
              const wikidataValue = wikidataField?.value;
              
              if (!hasRealValue(wikidataValue)) return null;
              
              return (
                <View style={{ 
                  flexDirection: 'row', 
                  alignItems: 'center', 
                  paddingVertical: 10,
                  borderTopWidth: displayedMeta.length > 0 ? 1 : 0,
                  borderTopColor: '#F3F4F6',
                  marginTop: displayedMeta.length > 0 ? 0 : 0
                }}>
                  <Text style={{ width: '30%', color: '#6B7280', fontSize: 15 }}>Wikidata</Text>
                  <View style={{ width: '70%', flexDirection: 'row', alignItems: 'center' }}>
                    <TouchableOpacity 
                      onPress={() => {
                        const url = String(wikidataValue);
                        if (typeof window !== 'undefined') {
                          window.open(url.startsWith('http') ? url : `https://${url}`, '_blank');
                        }
                      }}
                      style={{ 
                        flexDirection: 'row', 
                        alignItems: 'center',
                        minWidth: 32
                      }}
                    >
                      <SiWikidata style={{ color: '#006699', fontSize: 22 }} />
                    </TouchableOpacity>
                  </View>
                </View>
              );
            })()}
            
            {/* Wikipedia - 只在有值时显示 */}
            {(() => {
              const wikipediaField = rawMeta.find(p => String(p?.label || '').trim().toLowerCase() === 'wikipedia');
              const wikipediaValue = wikipediaField?.value;
              
              if (!hasRealValue(wikipediaValue)) return null;
              
              return (
                <View style={{ 
                  flexDirection: 'row', 
                  alignItems: 'center', 
                  paddingVertical: 10,
                  borderTopWidth: 1,
                  borderTopColor: '#F3F4F6'
                }}>
                  <Text style={{ width: '30%', color: '#6B7280', fontSize: 15 }}>Wikipedia</Text>
                  <View style={{ width: '70%', flexDirection: 'row', alignItems: 'center' }}>
                    <TouchableOpacity 
                      onPress={() => {
                        const url = String(wikipediaValue);
                        if (typeof window !== 'undefined') {
                          window.open(url.startsWith('http') ? url : `https://${url}`, '_blank');
                        }
                      }}
                      style={{ 
                        flexDirection: 'row', 
                        alignItems: 'center',
                        minWidth: 32
                      }}
                    >
                      <FaWikipediaW style={{ color: '#000000', fontSize: 22 }} />
                    </TouchableOpacity>
                  </View>
                </View>
              );
            })()}
            
            {/* 网络平台 - 只在有值时显示 */}
            {(() => {
              const websiteField = rawMeta.find(p => isWebsiteLabel(p?.label));
              const websiteValue = websiteField?.value || cardData.base_payload?.website || cardData.base_payload?.url || cardData.custom_payload?.website || cardData.custom_payload?.url;
              
              // 只在有实际值时显示
              if (!hasRealValue(websiteValue)) return null;

              const ensureArray = (v: any): string[] => {
                if (Array.isArray(v)) return v.map(String);
                const raw = String(v || '').trim();
                return raw ? [raw] : [];
              };
              
              const urls = ensureArray(websiteValue);
              if (urls.length === 0) return null;
              
              return (
                <View style={{ 
                  flexDirection: 'row', 
                  alignItems: 'center', 
                  paddingVertical: 10,
                  borderTopWidth: 1,
                  borderTopColor: '#F3F4F6'
                }}>
                  <Text style={{ width: '30%', color: '#6B7280', fontSize: 15 }}>网络平台</Text>
                  <View style={{ width: '70%', flexDirection: 'row', alignItems: 'center', flexWrap: 'wrap', gap: 12 }}>
                    {urls.map((urlItem, idx) => {
                      const url = urlItem.trim();
                      if (!url) return null;
                      const { Icon, color } = pickSocialIcon(url);
                      
                      return (
                        <TouchableOpacity 
                          key={idx}
                          onPress={() => {
                            if (typeof window !== 'undefined') {
                              window.open(url.startsWith('http') ? url : `https://${url}`, '_blank');
                            }
                          }}
                          style={{ 
                            flexDirection: 'row', 
                            alignItems: 'center',
                            minWidth: 32
                          }}
                        >
                          <Icon style={{ color, fontSize: 22 }} />
                        </TouchableOpacity>
                      );
                    })}
                  </View>
                </View>
              );
            })()}
            
            {/* Tags 显示 - 仿照截图的灰色标签设计 */}
            {Array.isArray(tags) && tags.length > 0 && (
              <View style={{ marginTop: 16 }}>
                <View style={{ height: 1, backgroundColor: '#E5E7EB', marginVertical: 12 }} />
                <View style={{ flexDirection: 'row', alignItems: 'center', marginBottom: 12 }}>
                  <PlatformIcon name="label" size={20} color="#007AFF" family="MaterialIcons" />
                  <Text style={{ fontSize: 16, fontWeight: '600', color: '#111827', marginLeft: 8 }}>标签</Text>
                </View>
                <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 8 }}>
                  {tags.map((tag, i) => (
                    <TouchableOpacity 
                      key={`${tag}-${i}`} 
                      style={{
                        backgroundColor: '#F3F4F6',
                        paddingHorizontal: 12,
                        paddingVertical: 8,
                        borderRadius: 6,
                        flexDirection: 'row',
                        alignItems: 'center'
                      }}
                      onPress={() => handleTagClick(tag)}
                      activeOpacity={0.7}
                    >
                      <Text style={{ 
                        color: '#6B7280', 
                        fontSize: 14,
                        fontWeight: '500'
                      }}>
                        # {tag}
                      </Text>
                    </TouchableOpacity>
                  ))}
                </View>
              </View>
            )}
          </>
        ) : activeTab === 'description' ? (
          <View style={{ paddingVertical: 4 }}>
            <Text style={{ color: '#8E8E93', lineHeight: 24, paddingLeft: 0 }}>
              {description.trim().length > 0 ? description : '未提供'}
            </Text>
          </View>
        ) : activeTab === 'nearby' ? (
          <View style={{ paddingVertical: 4 }}>
            <View style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
              <Text style={{ fontSize: 16, fontWeight: '600', color: '#111827' }}>附近景点</Text>
              {nearbyLoading ? <ActivityIndicator size="small" color="#0B61D6" /> : null}
            </View>
            {coords ? (
              <>
                <NearbyMap
                  center={[coords.lat, coords.lng]}
                  primaryLabel={title || '当前地点'}
                  spots={nearbySpots}
                  primaryImageUrl={primaryImageUrl}
                  primaryName={title}
                  primaryAddress={address}
                  onSelectSpot={onSelectNearby}
                />
                {nearbyError ? (
                  <Text style={{ fontSize: 13, color: '#DC2626', marginTop: 12 }}>{nearbyError}</Text>
                ) : (!nearbyLoading && nearbySpots.length === 0) ? (
                  <Text style={{ fontSize: 13, color: '#9CA3AF', marginTop: 12 }}>附近暂时没有其他公开卡片。</Text>
                ) : null}
              </>
            ) : (
              <Text style={{ fontSize: 14, color: '#9CA3AF' }}>暂时无法获取该地点的坐标，无法展示附近景点。</Text>
            )}
          </View>
        ) : (
          // Booking tab - 使用和原始模态相同的 BookingPlatformsRow 组件
          <View style={{ paddingVertical: 4 }}>
            <BookingPlatformsRow
              mode={'view'}
              supabaseClient={supabaseClient}
              cardId={cardData.id}
              displayVariant={(cardType === 'lodging' ? 'lodging' : 'restaurant') as any}
              platformProductId={cardData.base_payload?.platform_product_id || cardData.custom_payload?.platform_product_id}
              platformProductIds={cardData.base_payload?.platform_product_ids || cardData.custom_payload?.platform_product_ids}
              hideLabel={true}
              cardTitle={title}
              cardAddress={address}
              labels={{
                searchOn: (brand) => t('cardDetail.affiliate.searchOn', { brand }),
                book: t('cardDetail.tabs.book'),
              }}
            />
          </View>
        )}
      </View>
    </>
  );
};

const CardDetailPage: React.FC<CardDetailPageProps> = ({ isModal = false, onClose }) => {
  const { t } = useTravelI18n();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  // 全局吐司（Hook 必须在组件顶层调用）
  const { showSuccess, showError, showInfo } = useGlobalToast();
  // 检查是否显示返回按钮（用于判断是否外部打开）
  const showBack = useShowBackButton();
  
  // 检测是否从 modal 新开的标签页（URL 参数 from=modal）
  const isOpenedFromModal = typeof window !== 'undefined' && new URLSearchParams(window.location.search).get('from') === 'modal';
  
  const [cardData, setCardData] = useState<any>(null);
  const [creator, setCreator] = useState<any>(null);
  const [isFollowing, setIsFollowing] = useState<boolean>(false);
  const [isLiked, setIsLiked] = useState<boolean>(false);
  const [loading, setLoading] = useState(true);
  const [dataReady, setDataReady] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showMapModal, setShowMapModal] = useState(false);
  const [nearbySpots, setNearbySpots] = useState<NearbySpot[]>([]);
  const [nearbyLoading, setNearbyLoading] = useState(false);
  const [nearbyError, setNearbyError] = useState<string | null>(null);
  const [currentUserId, setCurrentUserId] = useState<string | null>(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [activeEditTab, setActiveEditTab] = useState<'details' | 'description'>('details');
  const fileInputRef = React.useRef<HTMLInputElement>(null);

  // Cover image handling MUST be declared before any early returns to keep hook order stable
  const videoPoster = cardData?.base_payload?.embed?.poster;
  const rawCover = cardData?.cover_image || cardData?.base_payload?.cover_image || cardData?.image || cardData?.imageUrl || videoPoster || '';
  const [coverImageUrl, setCoverImageUrl] = React.useState<string>(() => {
    if (rawCover) return rawCover;
    // Static map service is unreliable, use placeholder instead
    // Placeholder file is missing, return empty to allow fallbacks (e.g. letter icon in map)
    return '';
  });
  // Update cover when data changes
  React.useEffect(() => {
    if (rawCover) { 
      setCoverImageUrl(rawCover); 
      return; 
    }
    // Static map service is unreliable, use placeholder instead
    setCoverImageUrl('');
  }, [rawCover]);

  const {
    editMode,
    setEditMode,
    saving,
    localTitle,
    setLocalTitle,
    localDescription,
    setLocalDescription,
    localImage,
    localAddress,
    setLocalAddress,
    localMeta,
    setLocalMeta,
    localTags,
    setLocalTags,
    patchBasePayload,
    startEdit,
    save,
    onCoverFileSelected,
    derivedCardType,
  } = usePoiEditing({
    card: cardData,
    imageUrl: coverImageUrl,
    supabaseClient,
    currentUserId: currentUserId || undefined,
    onLoadedCanonical: (updated) => {
        setCardData(updated);
        if (updated.cover_image) setCoverImageUrl(updated.cover_image);
    },
  });

  const canEdit = isAdmin || (currentUserId && cardData && (currentUserId === cardData.creator_id || currentUserId === cardData.user_id || currentUserId === cardData.created_by));

  const modalBlocking = showAddModal || showMapModal;
  useLockBodyScroll(modalBlocking);

  // Try to extract coordinates from cardData (various payload shapes)
  const extractCoords = (card: any): { lat: number; lng: number } | undefined => {
    if (!card) return undefined;
    
    // Debug log for coordinates extraction
    // console.log('[CardDetailPage] Extracting coords from:', { 
    //   id: card.id, 
    //   lat: card.latitude, 
    //   lng: card.longitude,
    //   baseLoc: card.base_payload?.location 
    // });

    const tryObj = (obj: any) => {
      if (!obj) return undefined;
      
      const parseNum = (v: any) => {
        if (typeof v === 'number') return v;
        if (typeof v === 'string' && v.trim()) {
          const n = Number(v);
          return Number.isNaN(n) ? undefined : n;
        }
        return undefined;
      };

      // Handle array [lng, lat] or [lat, lng] directly
      if (Array.isArray(obj) && obj.length >= 2) {
         const n1 = parseNum(obj[0]);
         const n2 = parseNum(obj[1]);
         if (n1 !== undefined && n2 !== undefined) {
            // Heuristic: if abs(n1) > 90, it's likely lng.
            if (Math.abs(n1) > 90) return { lat: n2, lng: n1 };
            return { lat: n1, lng: n2 };
         }
      }

      // common shapes: { lat, lng } or { latitude, longitude } or { lat, lon }
      const lat1 = parseNum(obj.lat);
      const lng1 = parseNum(obj.lng) ?? parseNum(obj.lon);
      if (lat1 !== undefined && lng1 !== undefined) return { lat: lat1, lng: lng1 };

      const lat2 = parseNum(obj.latitude);
      const lng2 = parseNum(obj.longitude);
      if (lat2 !== undefined && lng2 !== undefined) return { lat: lat2, lng: lng2 };

      // coordinates array inside object
      if (Array.isArray(obj.coordinates) && obj.coordinates.length >= 2) {
        const a = obj.coordinates;
        const maybeLng = parseNum(a[0]);
        const maybeLat = parseNum(a[1]);
        if (maybeLat !== undefined && maybeLng !== undefined) return { lat: maybeLat, lng: maybeLng };
      }
      
      // string like "lat,lng" or "lng,lat"
      if (typeof obj === 'string' && obj.indexOf(',') !== -1) {
        const parts = obj.split(',').map(s => s.trim()).map(Number);
        if (parts.length >= 2 && parts.every(p => !Number.isNaN(p))) {
          if (Math.abs(parts[0]) <= 90) return { lat: parts[0], lng: parts[1] };
          return { lat: parts[1], lng: parts[0] };
        }
      }
      return undefined;
    };

    // check typical locations
    // Prioritize top-level card fields (latitude/longitude)
    const candidates = [
      card, 
      card.base_payload, 
      card.custom_payload, 
      card.base_payload?.location, 
      card.custom_payload?.location, 
      card.location, 
      card.address,
      card.base_payload?.geometry // GeoJSON support
    ];
    for (const c of candidates) {
      const r = tryObj(c);
      if (r) return r;
    }
    return undefined;
  };

  const coords = extractCoords(cardData);
  
  // Debug log
  React.useEffect(() => {
    if (cardData) {
      console.log('[CardDetailPage] Coords extracted:', coords, 'from card:', cardData.id);
    }
  }, [cardData, coords]);

  const handleNearbySpotSelect = React.useCallback(
    (spotId: string) => {
      if (!spotId) return;
      navigate(`/card/${spotId}`);
    },
    [navigate]
  );

  React.useEffect(() => {
    if (!coords || !cardData?.id) {
      setNearbySpots([]);
      setNearbyError(null);
      setNearbyLoading(false);
      return;
    }

    let cancelled = false;
    const fetchNearby = async () => {
      if (cancelled) return;
      setNearbyLoading(true);
      setNearbyError(null);
      try {
        // delta 0.045 ≈ 5 公里範圍
        const delta = 0.045;
        let query: any = supabaseClient
          .from('cards_cards')
          .select('id,title,address,description,card_type,latitude,longitude,cover_image,base_payload')
          .eq('status', 'active')
          .eq('visibility', 'public')
          .neq('id', cardData.id)
          .gte('latitude', coords.lat - delta)
          .lte('latitude', coords.lat + delta)
          .gte('longitude', coords.lng - delta)
          .lte('longitude', coords.lng + delta)
          .limit(20);

        const { data, error } = await query;
        if (error) throw error;

        const extractAddressString = (value: any): string | undefined => {
          if (!value) return undefined;
          if (typeof value === 'string') {
            const trimmed = value.trim();
            return trimmed.length ? trimmed : undefined;
          }
          if (Array.isArray(value)) {
            const joined = value
              .map((item) => (typeof item === 'string' ? item.trim() : ''))
              .filter(Boolean)
              .join(' ')
              .trim();
            return joined.length ? joined : undefined;
          }
          if (typeof value === 'object') {
            const keys = ['formatted', 'fullAddress', 'address', 'name', 'street', 'city', 'district', 'province', 'state'];
            const parts = keys
              .map((key) => {
                const payload = (value as Record<string, unknown>)[key];
                return typeof payload === 'string' ? payload.trim() : undefined;
              })
              .filter((segment): segment is string => !!segment && segment.length > 0);
            if (parts.length > 0) return parts.join(' ');
          }
          return undefined;
        };

        const spots = (data || [])
          .map((row: any) => {
            const lat = typeof row.latitude === 'number' ? row.latitude : Number(row.latitude);
            const lng = typeof row.longitude === 'number' ? row.longitude : Number(row.longitude);
            if (Number.isNaN(lat) || Number.isNaN(lng)) return null;
            const name = row.title || '未命名地点';
            const description = typeof row.description === 'string' ? row.description : '';
            let base: any = row.base_payload;
            if (base && typeof base === 'string') {
              try { base = JSON.parse(base); } catch (e) { base = {}; }
            }
            if (!base || typeof base !== 'object') base = {};
            const imageCandidate = row.cover_image || base.cover_image || base.image || base.image_url || base.imageUrl;
            const imageUrl = typeof imageCandidate === 'string' ? imageCandidate : undefined;
            const addressCandidate =
              extractAddressString(row.address) ||
              extractAddressString(base.address) ||
              extractAddressString(base.location_name) ||
              extractAddressString(base.placeName) ||
              extractAddressString(base.location) ||
              extractAddressString(description);
            const addressText = addressCandidate || undefined;

            // Determine category: prefer explicit card_type, then base payload hints
            const rawCategory = (row.card_type || base.card_type || base.type || base.category || '') as string;
            const category = typeof rawCategory === 'string' && rawCategory.trim() ? rawCategory.trim().toLowerCase() : undefined;

            return {
              id: row.id,
              name,
              description,
              lat,
              lng,
              imageUrl,
              address: addressText,
              category,
              source: 'card',
            } as NearbySpot;
          })
          .filter(Boolean) as NearbySpot[];

        if (!cancelled) setNearbySpots(spots);
      } catch (err) {
        console.error('[CardDetailPage] fetch nearby error', err);
        if (!cancelled) setNearbyError(err instanceof Error ? err.message : '加载附近景点失败');
      } finally {
        if (!cancelled) setNearbyLoading(false);
      }
    };

    fetchNearby();
    return () => {
      cancelled = true;
    };
  }, [coords?.lat, coords?.lng, cardData?.id]);



  // Debug: log card data when it changes (must be at top level)
  React.useEffect(() => {
    if (cardData) {
      console.log('[CardDetailPage] Card data loaded:', {
        id: cardData.id,
        title: cardData.title,
        description: cardData.description,
        address: cardData.address,
        card_type: cardData.card_type,
        hasBasePayload: !!cardData.base_payload,
        basePayloadTitle: cardData.base_payload?.title,
        basePayloadAddress: cardData.base_payload?.address,
        basePayloadDescription: cardData.base_payload?.description,
      });
    }
  }, [cardData]);

  // 使用 SEO Hook (仅在非 Modal 模式下生效)
  useCardSEO(
    cardData ? {
      id: cardData.id,
      title: cardData.title || 'Card',
      description: cardData.description || cardData.base_payload?.description || '',
      image: cardData.cover_image || cardData.base_payload?.cover_image || '',
      cardType: cardData.card_type || 'card',
      location: cardData.address || cardData.base_payload?.address || '',
      priceRange: cardData.price_range || cardData.base_payload?.price_range || '',
    } : {
      id: id || 'unknown',
      title: 'Card',
      description: 'Loading card details...',
      cardType: 'card',
    }
  );

  // 从 URL 加载卡片数据
  useEffect(() => {
    if (!id) {
      setError('Missing card ID');
      setLoading(false);
      return;
    }

    const loadCardData = async () => {
      try {
        setLoading(true);
        setDataReady(false);
        
        // 用 @cubemeet/core 中的 Supabase 客户端
        const { supabase } = await import('@cubemeet/core/services/supabase');
        
        // NOTE: removed mount-time auth check to avoid redundant user lookups on page open.
        // Auth will be checked lazily when the user performs actions (e.g. add/follow).
        
        const { data, error: dbError } = await supabase
          .from('cards_cards')
          .select('*, image_attributions')
          .eq('id', id)
          .eq('status', 'active')
          .single();
        
        if (dbError) {
          setError(dbError.message || 'Card not found');
          setCardData(null);
          setCreator(null);
          return;
        }

        if (!data) {
          setError('Card not found');
          setCardData(null);
          setCreator(null);
          return;
        }

        setCardData(data);
        setError(null);
        
        // Debug: log loaded card data
        console.log('[CardDetailPage] Loaded card data:', {
          id: data.id,
          title: data.title,
          description: data.description,
          address: data.address,
          card_type: data.card_type,
          cover_image: data.cover_image,
          tags: data.tags,
          hasBasePayload: !!data.base_payload,
          basePayloadKeys: data.base_payload ? Object.keys(data.base_payload) : [],
          basePayloadMetaPairs: data.base_payload?.metaPairs,
        });
        
        // 获取创建人信息
        const creatorId = data.creator_id || data.user_id || data.created_by;
        if (creatorId) {
          try {
            const { data: userData, error: userError } = await supabase
              .from('profiles')
              .select('id, display_name, avatar_url, bio')
              .eq('id', creatorId)
              .single();

            if (userData && !userError) {
              setCreator({
                id: userData.id,
                display_name: userData.display_name || 'Unknown User',
                avatar_url: userData.avatar_url,
                bio: userData.bio,
              });
              console.log('✅ Creator loaded:', userData);
              
              // Check like status if user is logged in
              const authRes: any = await supabase.auth.getUser();
              const currentUser = authRes?.data?.user;
              if (currentUser) {
                 setCurrentUserId(currentUser.id);
                 // Check admin status
                 const { data: profile } = await supabase.from('profiles').select('is_admin').eq('id', currentUser.id).single();
                 if (profile?.is_admin) setIsAdmin(true);

                 try {
                   const liked = await checkIsUserLiked(currentUser.id, userData.id);
                   setIsLiked(liked);
                 } catch (e) {
                   console.warn('Failed to check like status:', e);
                 }
              }
                // NOTE: defer follow-status check to the follow action to avoid
                // checking auth on every page open. Initial follow state remains false.
            }
          } catch (e) {
            console.warn('Failed to load creator info:', e);
          }
        }

  // Debug: 输出数据结构
        console.log('✅ Card data loaded:', data);
        console.log('Card fields:', Object.keys(data || {}));
        console.log('Title:', data?.title, 'Address:', data?.address);
        console.log('Creator info:', { creator_id: data?.creator_id, user_id: data?.user_id, created_by: data?.created_by });
        
        // 所有資料（card + creator + follow）已嘗試載入，標記為 ready
        setDataReady(true);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load card');
        setCardData(null);
        setCreator(null);
        setDataReady(false);
      } finally {
        setLoading(false);
      }
    };

    loadCardData();
  }, [id]);

  const handleClose = () => {
    if (isModal && onClose) {
      onClose();
    } else if (isOpenedFromModal && typeof window !== 'undefined') {
      // 从 modal 新开的标签页，关闭当前标签
      window.close();
    } else {
      navigate(-1);
    }
  };

  // 点击封面右上角的 + 按钮的统一处理
  const handleAddPress = async () => {
    try {
      // 使用顶部导入的 supabase 客户端检查当前用户
      const authRes: any = await supabaseClient.auth.getUser();
      const currentUser = authRes?.data?.user || (supabaseClient.auth && (supabaseClient.auth.user && supabaseClient.auth.user()));

      if (!currentUser) {
        // 未登录：导航到 auth 页面，并带上 referer（编码后的当前路径）以便登录后返回
        const referer = typeof window !== 'undefined' ? encodeURIComponent(window.location.pathname + window.location.search) : '/';
        navigate(`/auth?referer=${referer}`);
        return;
      }

      // 已登录：打开添加到行程的 modal（PoC）
      console.log('CardDetailPage: + pressed and user is logged in -> userId=', currentUser.id);
      setShowAddModal(true);
    } catch (err) {
      console.error('handleAddPress error:', err);
      try {
        const referer = typeof window !== 'undefined' ? encodeURIComponent(window.location.pathname + window.location.search) : '/';
        navigate(`/auth?referer=${referer}`);
      } catch (e) {
        console.error('navigate fallback failed:', e);
      }
    }
  };

  // 获取窗口尺寸，用于响应式布局
  const { width } = Dimensions.get('window');
  const isMobile = width <= 900;

  

  const showLoading = loading || !dataReady;

  const handleModalAdded = (entry: any) => {
    console.log('AddToItineraryModal added entry:', entry);
    // TODO: 可在此更新本地缓存或触发 UI 刷新
  };

  const handleFollowPress = async (targetId?: string) => {
    try {
      const { supabase } = await import('@cubemeet/core/services/supabase');
      const authRes: any = await supabase.auth.getUser();
      const currentUser = authRes?.data?.user || (supabase.auth && (supabase.auth.user && supabase.auth.user()));
      if (!currentUser) {
        const ref = typeof window !== 'undefined' ? encodeURIComponent(window.location.pathname + window.location.search) : '/';
        navigate(`/auth?referer=${ref}`);
        return;
      }
      if (!targetId) return;

      const { data, error } = await supabase
        .from('friends')
        .upsert({ user_id: currentUser.id, friend_id: targetId, status: 'active' }, { onConflict: 'user_id,friend_id' })
        .select()
        .single();

      if (error) {
        const msg = String(error.message || error?.details || '关注失败');
        if (/conflict|duplicate|unique|23505|409/i.test(msg) || (error.status === 409)) {
          try {
            const { data: existing } = await supabase
              .from('friends')
              .select('*')
              .eq('user_id', currentUser.id)
              .eq('friend_id', targetId)
              .maybeSingle();
            if (existing) {
              setIsFollowing(true);
              showSuccess && showSuccess('已關注');
              return;
            }
          } catch (checkErr) {
            console.error('Follow existence check failed:', checkErr);
          }
          setIsFollowing(true);
          showSuccess && showSuccess('已關注');
          return;
        }
        showError && showError(msg);
        return;
      }

      setIsFollowing(true);
      showSuccess && showSuccess('已關注');
    } catch (e: any) {
      console.error('Unexpected follow error:', e);
      try { showError && showError(String(e?.message || '关注失败')); } catch (er) { console.error(er); }
    }
  };

  const handleLikePress = async () => {
    try {
      const { supabase } = await import('@cubemeet/core/services/supabase');
      const authRes: any = await supabase.auth.getUser();
      const currentUser = authRes?.data?.user || (supabase.auth && (supabase.auth.user && supabase.auth.user()));
      
      if (!currentUser) {
        const ref = typeof window !== 'undefined' ? encodeURIComponent(window.location.pathname + window.location.search) : '/';
        navigate(`/auth?referer=${ref}`);
        return;
      }
      
      if (!creator?.id) return;

      if (isLiked) {
        await unlikeUser(currentUser.id, creator.id);
        setIsLiked(false);
        showSuccess && showSuccess('已取消點讚');
      } else {
        await likeUser(currentUser.id, creator.id);
        setIsLiked(true);
        showSuccess && showSuccess('已點讚');
      }
    } catch (e: any) {
      console.error('Like error:', e);
      showError && showError(e.message || '操作失败');
    }
  };

  // 顯示全螢幕 Loading（在 modal 模式下用 FullscreenWrapper）
  if (showLoading) {
    const loader = (
      <View style={styles.container}>
        <View style={styles.centerContainer}>
          <ActivityIndicator size="large" color="#0ea5a4" />
          <Text style={styles.loadingText}>正在加載卡片詳情...</Text>
        </View>
      </View>
    );

    if (isModal) {
      return (
        <FullscreenWrapper visible={true} onClose={handleClose}>
          {loader}
        </FullscreenWrapper>
      );
    }

    return loader;
  }

  const isVideoCard = String(cardData?.card_type || '').toLowerCase() === 'video' || !!cardData?.base_payload?.embed;
  const videoEmbed = cardData?.base_payload?.embed;
  const videoSrc = typeof videoEmbed?.src === 'string' ? videoEmbed.src : undefined;

  // 错误状态
  if (error || !cardData) {
    console.log('[CardDetailPage] Error state:', { error, hasCardData: !!cardData, cardData });
    return (
      <View style={styles.container}>
        <View style={styles.centerContainer}>
          <Text style={styles.errorText}>❌ {error || 'Card not found'}</Text>
        </View>
      </View>
    );
  }

  // Modal 模式: 使用 FullscreenWrapper 显示
  if (isModal) {
    return (
      <FullscreenWrapper visible={true} onClose={handleClose}>
        <View style={styles.modalContainer}>
          {/* Cover / Video */}
          <View style={styles.coverWrapper}>
            {isVideoCard && videoSrc && Platform.OS === 'web' ? (
              <iframe
                src={videoSrc}
                style={{ border: 'none', width: '100%', height: '100%' }}
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                allowFullScreen
                title={cardData.title || 'video'}
              />
            ) : (coverImageUrl && !coverImageUrl.includes('placeholders')) ? (
              <Image accessibilityLabel="Card cover image" source={{ uri: coverImageUrl }} style={styles.coverImage} />
            ) : coords ? (
              <NearbyMap
                center={[coords.lat, coords.lng]}
                spots={[]}
                primaryLabel={cardData.title}
                primaryName={cardData.title}
                enableOSM={false}
                showInterPointDistances={false}
                height="100%"
                showFullscreenButton={false}
                showControls={false}
              />
            ) : coverImageUrl ? (
              <View style={styles.coverFallback}>
                <Text style={styles.coverFallbackText}>地图加载失败，使用占位封面</Text>
              </View>
            ) : (
              <View style={styles.coverFallback}>
                <Text style={styles.coverFallbackText}>无封面</Text>
              </View>
            )}
              {/* Back button - only show if there's history */}
              {showBack && (
                <TouchableOpacity
                  style={styles.backButton}
                  activeOpacity={0.8}
                  onPress={handleClose}
                >
                  <PlatformIcon name="arrow-back" size={22} color="#fff" />
                </TouchableOpacity>
              )}
              {/* Floating add button (top-right over cover) */}
              <TouchableOpacity
                style={styles.addButton}
                activeOpacity={0.8}
                onPress={handleAddPress}
              >
                <PlatformIcon name="add" size={22} color="#fff" />
              </TouchableOpacity>
              {/* Image attributions */}
              {cardData?.image_attributions && Array.isArray(cardData.image_attributions) && cardData.image_attributions.length > 0 && (
                <View style={styles.imageAttributions}>
                  {cardData.image_attributions.map((attribution: string, index: number) => (
                    <Text
                      key={index}
                      style={styles.imageAttributionText}
                      dangerouslySetInnerHTML={{ __html: attribution }}
                    />
                  ))}
                </View>
              )}
          </View>
          
          {/* 分享按钮 - 只在 dataReady 時顯示，避免閃爍 */}
          {dataReady && (
            <View style={{ marginBottom: -16 }}>
              <ShareButton
                type="card"
                id={cardData.id}
                title={cardData.title}
                description={cardData.description}
                imageUrl={coverImageUrl}
                creator={creator}
                showNativeShare={true}
                showCopyLink={true}
                onCreatorPress={() => creator?.id && navigate(`/user/${creator.id}`)}
                userRelation={{ isFollowing, isLiked }}
                onFollowPress={handleFollowPress}
                onLikePress={handleLikePress}
              />
            </View>
          )}
          
          {/* 主内容 - 可滚动区域 */}
          <ScrollView 
            contentContainerStyle={styles.scrollContent} 
            scrollEnabled={!modalBlocking}
            style={modalBlocking ? { overflow: 'hidden' } : undefined}
          >
            <View style={styles.content}>
              <View style={[styles.form, styles.formContent]}>
                {/* 根据卡片类型渲染不同的编辑器 */}
                {['restaurant', 'lodging', 'poi', 'activity', 'shopping', 'video', 'osm'].includes(cardData.card_type) && (
                  <View style={styles.editorContainer}>
                    <CardWithTabs 
                      cardData={cardData}
                      cardType={cardData.card_type}
                      buildPoiMeta={buildPoiMeta}
                      supabaseClient={supabaseClient}
                      onOpenMapModal={() => setShowMapModal(true)}
                    />
                  </View>
                )}

                {/* Image Gallery - 显示所有图片 */}
                {cardData?.id && (
                  <View style={{ marginTop: 24, paddingHorizontal: 16 }}>
                    <CardImageGallery
                      cardId={cardData.id}
                      currentUserId={currentUserId}
                      gallery={cardData.image_gallery || []}
                      onUpdate={async (updatedGallery) => {
                        try {
                          const { error } = await supabaseClient
                            .from('cards_cards')
                            .update({ image_gallery: updatedGallery })
                            .eq('id', cardData.id);

                          if (error) throw error;

                          // 更新本地 state
                          setCardData((prev: any) => ({
                            ...prev,
                            image_gallery: updatedGallery,
                          }));
                        } catch (err) {
                          console.error('Failed to update image gallery:', err);
                          throw err;
                        }
                      }}
                      canUpload={true}
                      showAllImages={true}
                      isAdmin={false}
                      labels={{
                        allPhotos: t('cardDetail.gallery.allPhotos'),
                        tripPhotos: t('cardDetail.gallery.tripPhotos'),
                        addPhoto: t('cardDetail.gallery.addPhoto'),
                      }}
                    />
                  </View>
                )}

                {/* 其他未知卡片类型的占位符 */}
                {!['restaurant','lodging','poi','activity','shopping','video','osm'].includes(String(cardData.card_type || '').toLowerCase()) && (
                  <View style={styles.placeholderContainer}>
                    <Text style={styles.placeholderText}>
                      {cardData.card_type} card details coming soon...
                    </Text>
                  </View>
                )}

                {/* 底部 padding */}
                <View style={styles.spacer} />
              </View>
            </View>
          </ScrollView>
        </View>
          <AddToItineraryModal visible={showAddModal} onClose={() => setShowAddModal(false)} cardSnapshot={{ id: cardData?.id, title: cardData?.title, cover_image: coverImageUrl, address: cardData?.address }} onAdded={handleModalAdded} />
          <MapSelectionModal visible={showMapModal} onClose={() => setShowMapModal(false)} address={cardData?.address || cardData?.base_payload?.address || ''} coords={coords} />
        </FullscreenWrapper>
    );
  }

  // 独立页面模式: 完整页面显示 (加上 cover 图片)
  return (
    <View style={styles.container}>
      {/* Cover 图片 */}
      <View style={styles.content}>
        <View style={styles.form}>
          {/* Cover / Video */}
          <View style={styles.coverWrapper}>
            {isVideoCard && videoSrc && Platform.OS === 'web' ? (
              <iframe
                src={videoSrc}
                style={{ border: 'none', width: '100%', height: '100%' }}
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                allowFullScreen
                title={cardData.title || 'video'}
              />
            ) : ((localImage || coverImageUrl) && !(localImage || coverImageUrl).includes('placeholders')) ? (
              <Image source={{ uri: localImage || coverImageUrl }} style={styles.coverImage} />
            ) : coords ? (
              <NearbyMap
                center={[coords.lat, coords.lng]}
                spots={[]}
                primaryLabel={cardData.title}
                primaryName={cardData.title}
                enableOSM={false}
                showInterPointDistances={false}
                height="100%"
                showFullscreenButton={false}
                showControls={false}
              />
            ) : coverImageUrl ? (
              <View style={styles.coverFallback}>
                <Text style={styles.coverFallbackText}>地图加载失败，使用占位封面</Text>
              </View>
            ) : (
              <View style={styles.coverFallback}>
                <Text style={styles.coverFallbackText}>无封面</Text>
              </View>
            )}
              {/* Back/Close button - show X if opened from modal, arrow if normal navigation */}
              {(showBack || isOpenedFromModal) && (
                <TouchableOpacity
                  style={styles.backButton}
                  activeOpacity={0.8}
                  onPress={handleClose}
                >
                  <PlatformIcon name={isOpenedFromModal ? "close" : "arrow-back"} size={22} color="#fff" />
                </TouchableOpacity>
              )}
              {/* Floating buttons (top-right over cover) */}
              <View style={{ position: 'absolute', top: 16, right: 16, flexDirection: 'row', gap: 12, zIndex: 2000 }}>
                {editMode ? (
                  <>
                    <TouchableOpacity
                      style={styles.roundButton}
                      activeOpacity={0.8}
                      onPress={() => fileInputRef.current?.click()}
                    >
                      <PlatformIcon name="photo_camera" size={22} color="#fff" family="MaterialIcons" />
                    </TouchableOpacity>
                    <TouchableOpacity
                      style={[styles.roundButton, { backgroundColor: '#0ea5a4' }]}
                      activeOpacity={0.8}
                      onPress={save}
                      disabled={saving}
                    >
                      {saving ? <ActivityIndicator size="small" color="#fff" /> : <PlatformIcon name="check" size={22} color="#fff" family="MaterialIcons" />}
                    </TouchableOpacity>
                  </>
                ) : (
                  <>
                    {canEdit && (
                      <TouchableOpacity
                        style={styles.roundButton}
                        activeOpacity={0.8}
                        onPress={startEdit}
                      >
                        <PlatformIcon name="edit" size={22} color="#fff" family="MaterialIcons" />
                      </TouchableOpacity>
                    )}
                    <TouchableOpacity
                      style={styles.roundButton}
                      activeOpacity={0.8}
                      onPress={handleAddPress}
                    >
                      <PlatformIcon name="add" size={22} color="#fff" />
                    </TouchableOpacity>
                  </>
                )}
              </View>

              {/* Hidden file input for cover image upload */}
              {Platform.OS === 'web' && (
                <input
                  ref={fileInputRef as any}
                  type="file"
                  accept="image/*"
                  style={{ display: 'none' }}
                  onChange={(e) => {
                    const file = e.target?.files?.[0];
                    if (file) {
                      onCoverFileSelected(file);
                    }
                  }}
                />
              )}
              {/* Image attributions */}
              {cardData?.image_attributions && Array.isArray(cardData.image_attributions) && cardData.image_attributions.length > 0 && (
                <View style={styles.imageAttributions}>
                  <Text style={styles.imageAttributionLabel}>图片来源：</Text>
                  {cardData.image_attributions.map((attribution: string, index: number) => {
                    const parsed = parseAttribution(attribution);
                    if (parsed.url) {
                      return (
                        <TouchableOpacity
                          key={index}
                          onPress={() => {
                            try {
                              if (typeof window !== 'undefined') {
                                window.open(parsed.url, '_blank', 'noopener');
                              }
                            } catch (e) {
                              console.error('Failed to open attribution link:', e);
                            }
                          }}
                          activeOpacity={0.8}
                          style={{ flexShrink: 1 }}
                        >
                          <Text 
                            style={[styles.imageAttributionText, styles.imageAttributionLink]}
                            numberOfLines={1}
                            ellipsizeMode="tail"
                          >
                            {parsed.text}
                          </Text>
                        </TouchableOpacity>
                      );
                    }
                    return (
                      <Text 
                        key={index} 
                        style={styles.imageAttributionText}
                        numberOfLines={1}
                        ellipsizeMode="tail"
                      >
                        {parsed.text}
                      </Text>
                    );
                  })}
                </View>
              )}
            </View>

          {/* 分享按钮 - 只在 dataReady 時顯示，避免閃爍 */}
          {dataReady && (
            <View style={{ marginBottom: -16 }}>
              <ShareButton
                type="card"
                id={cardData.id}
                title={cardData.title}
                description={cardData.description}
                imageUrl={coverImageUrl}
                creator={creator}
                showNativeShare={true}
                showCopyLink={true}
                onCreatorPress={() => creator?.id && navigate(`/user/${creator.id}`)}
                userRelation={{ isFollowing, isLiked }}
                onFollowPress={handleFollowPress}
                onLikePress={handleLikePress}
              />
            </View>
          )}
        </View>
      </View>

      {/* 主内容 - 可滚动区域 */}
      <ScrollView 
        contentContainerStyle={styles.scrollContent} 
        scrollEnabled={!modalBlocking}
        style={modalBlocking ? { overflow: 'hidden' } : undefined}
      >
        <View style={styles.content}>
          <View style={[styles.form, styles.formContent]}>
            {/* 根据卡片类型渲染统一的标签页组件 */}
            {editMode ? (
               <View style={styles.editorContainer}>
                  <PoiTitleAddressSection
                    editMode={true}
                    localTitle={localTitle}
                    onChangeTitle={setLocalTitle}
                    titleFallback={cardData.title}
                    localAddress={localAddress || ''}
                    onChangeAddress={setLocalAddress}
                    onNavigate={() => setShowMapModal(true)}
                  />
                  <CardTypeSelector
                    currentType={derivedCardType}
                    onSelect={(type) => patchBasePayload({ card_type: type })}
                  />
                  
                  {/* Edit Mode Tabs */}
                  <View style={{ marginTop: 16 }}>
                    <View style={styles.tabsRow}>
                      <TouchableOpacity
                        style={[styles.tabBtn, activeEditTab === 'details' && styles.tabBtnActive]}
                        onPress={() => setActiveEditTab('details')}
                      >
                        <Text style={[styles.tabText, activeEditTab === 'details' && styles.tabTextActive]}>
                          {t('cardDetail.tabs.details')}
                        </Text>
                      </TouchableOpacity>
                      <TouchableOpacity
                        style={[styles.tabBtn, activeEditTab === 'description' && styles.tabBtnActive]}
                        onPress={() => setActiveEditTab('description')}
                      >
                        <Text style={[styles.tabText, activeEditTab === 'description' && styles.tabTextActive]}>
                          {t('cardDetail.tabs.description')}
                        </Text>
                      </TouchableOpacity>
                    </View>
                    <View style={styles.tabDivider} />

                    {activeEditTab === 'details' && (
                      <DetailsTab
                        meta={localMeta}
                        editMode={true}
                        displayedMeta={localMeta}
                        setLocalMeta={setLocalMeta}
                        tags={localTags}
                        setTags={setLocalTags}
                        coordinates={coords}
                        onCoordinatesChange={(lat, lng) => patchBasePayload({ latitude: lat, longitude: lng })}
                      />
                    )}
                    
                    {activeEditTab === 'description' && (
                      <DescriptionTab
                        editMode={true}
                        value={localDescription}
                        fallback={cardData.description || ''}
                        onChange={setLocalDescription}
                      />
                    )}
                  </View>
               </View>
            ) : (
                ['restaurant', 'lodging', 'poi', 'activity', 'shopping', 'video', 'osm'].includes(cardData.card_type) && (
                  <View style={styles.editorContainer}>
                    <CardWithTabs 
                      cardData={cardData}
                      cardType={cardData.card_type}
                      buildPoiMeta={buildPoiMeta}
                      supabaseClient={supabaseClient}
                      onOpenMapModal={() => setShowMapModal(true)}
                      coords={coords || null}
                      nearbySpots={nearbySpots}
                      nearbyLoading={nearbyLoading}
                      nearbyError={nearbyError}
                      primaryImageUrl={coverImageUrl}
                      onSelectNearby={handleNearbySpotSelect}
                    />
                  </View>
                )
            )}

            {/* Image Gallery - 显示所有图片 */}
            {cardData?.id && (
              <View style={{ marginTop: 24, paddingHorizontal: 16 }}>
                <CardImageGallery
                  cardId={cardData.id}
                  currentUserId={currentUserId}
                  gallery={cardData.image_gallery || []}
                  onUpdate={async (updatedGallery) => {
                    try {
                      const { error } = await supabaseClient
                        .from('cards_cards')
                        .update({ image_gallery: updatedGallery })
                        .eq('id', cardData.id);

                      if (error) throw error;

                      // 更新本地 state
                      setCardData((prev: any) => ({
                        ...prev,
                        image_gallery: updatedGallery,
                      }));
                    } catch (err) {
                      console.error('Failed to update image gallery:', err);
                      throw err;
                    }
                  }}
                  canUpload={true}
                  showAllImages={true}
                  isAdmin={false}
                  labels={{
                    allPhotos: t('cardDetail.gallery.allPhotos'),
                    tripPhotos: t('cardDetail.gallery.tripPhotos'),
                    addPhoto: t('cardDetail.gallery.addPhoto'),
                  }}
                />
              </View>
            )}

            {/* 其他未知卡片类型的占位符（排除 video） */}
            {(() => {
              const lowerType = String(cardData.card_type || '').toLowerCase();
              const known = ['restaurant','lodging','poi','activity','shopping','video','osm'];
              if (known.includes(lowerType)) return null;
              return (
                <View style={styles.placeholderContainer}>
                  <Text style={styles.placeholderText}>
                    {cardData.card_type} card details coming soon...
                  </Text>
                </View>
              );
            })()}

            {/* 底部 padding */}
            <View style={styles.spacer} />
          </View>
        </View>
      </ScrollView>
      
      <AddToItineraryModal visible={showAddModal} onClose={() => setShowAddModal(false)} cardSnapshot={{ id: cardData?.id, title: cardData?.title, cover_image: coverImageUrl, address: cardData?.address }} onAdded={handleModalAdded} />
  <MapSelectionModal visible={showMapModal} onClose={() => setShowMapModal(false)} address={cardData?.address || cardData?.base_payload?.address || ''} coords={coords} />
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#f6f7f8',
  },
  modalContainer: {
    flex: 1,
    flexDirection: 'column',
    backgroundColor: '#fff',
  },
  centerContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    gap: 16,
  },
  loadingText: {
    fontSize: 16,
    color: '#666',
  },
  errorText: {
    fontSize: 16,
    color: '#d32f2f',
    textAlign: 'center',
  },
  scrollContent: {
    paddingHorizontal: 0,
    paddingTop: 16,
    paddingBottom: BOTTOM_NAV_GAP, // use shared bottom gap
  },
  content: {
    // 移除 flex: 1 避免内容垂直居中
  },
  form: {
    maxWidth: 600,
    width: '100%',
    alignSelf: 'center',
    backgroundColor: '#ffffff',
    borderRadius: 8,
    boxShadow: '0 6px 18px rgba(16,24,40,0.06)' as any,
    overflow: 'hidden' as any,
  },
  formContent: {
    paddingHorizontal: 16,
    paddingVertical: 16,
  },
  coverWrapper: {
    width: '100%',
    height: 240,
    backgroundColor: '#f0f0f0',
    overflow: 'hidden',
  },
  coverImage: {
    width: '100%',
    height: '100%',
    resizeMode: 'cover',
  },
  imageAttributions: {
    position: 'absolute' as any,
    bottom: 8,
    right: 8,
    backgroundColor: 'rgba(0, 0, 0, 0.7)',
    paddingHorizontal: 10,
    paddingVertical: 6,
    borderRadius: 6,
    maxWidth: '80%',
    flexDirection: 'row',
    alignItems: 'center',
  },
  imageAttributionLabel: {
    fontSize: 11,
    color: '#FFFFFF',
    lineHeight: 14,
    fontWeight: '600' as any,
    marginRight: 4,
  },
  imageAttributionText: {
    fontSize: 11,
    color: '#FFFFFF',
    lineHeight: 14,
    flexShrink: 1,
  },
  imageAttributionLink: {
    textDecorationLine: 'underline',
  },
  coverFallback: { flex: 1, alignItems: 'center', justifyContent: 'center', backgroundColor: '#f5f5f5' },
  coverFallbackText: { color: '#9CA3AF', fontSize: 14 },
  header: {
    marginBottom: 24,
    borderBottomWidth: 1,
    borderBottomColor: '#eee',
    paddingBottom: 16,
  },
  title: {
    fontSize: 24,
    fontWeight: '700',
    color: '#1a1a1a',
    marginBottom: 8,
  },
  description: {
    fontSize: 14,
    color: '#666',
    lineHeight: 20,
  },
  editorContainer: {
    marginBottom: 24,
  },
  placeholderContainer: {
    backgroundColor: '#f5f5f5',
    borderRadius: 12,
    padding: 24,
    alignItems: 'center',
    marginBottom: 24,
  },
  placeholderText: {
    fontSize: 14,
    color: '#999',
    fontStyle: 'italic',
  },
  shareSection: {
    marginTop: 24,
    paddingTop: 24,
    paddingHorizontal: 16,
    paddingBottom: 24,
    borderTopWidth: 1,
    borderTopColor: '#eee',
    backgroundColor: '#fafafa',
  },
  modalShareSection: {
    paddingHorizontal: 16,
    paddingVertical: 12,
    borderBottomWidth: 1,
    borderBottomColor: '#eee',
    backgroundColor: '#fafafa',
    flexShrink: 0,
    flexDirection: 'row',
    gap: 10,
  },
  shareButton: {
    flex: 1,
    paddingVertical: 10,
    paddingHorizontal: 12,
    backgroundColor: '#fff',
    borderRadius: 8,
    borderWidth: 1,
    borderColor: '#ddd',
    alignItems: 'center',
    justifyContent: 'center',
  },
  shareButtonText: {
    fontSize: 14,
    fontWeight: '600',
    color: '#0ea5a4',
  },
  spacer: {
    height: BOTTOM_SPACER_HEIGHT,
  },
  addButton: {
    position: 'absolute',
    top: 16,
    right: 16,
    width: 40,
    height: 40,
    borderRadius: 20,
    backgroundColor: '#FF6B35',
    justifyContent: 'center',
    alignItems: 'center',
    ...Platform.select({
      web: {
        boxShadow: '0px 2px 6px rgba(0,0,0,0.16)',
        borderWidth: 0.5,
        borderColor: 'rgba(0,0,0,0.06)',
        zIndex: 2000,
      },
      default: {
        elevation: 2,
        shadowColor: '#000',
        shadowOffset: { width: 0, height: 2 },
        shadowOpacity: 0.16,
        shadowRadius: 6,
        borderWidth: 0.5,
        borderColor: 'rgba(0,0,0,0.06)',
        zIndex: 2000,
      },
    }),
  },
 backButton: {
    position: 'absolute',
    top: 16,
    left: 16,
    width: 40,
    height: 40,
    borderRadius: 20,
    backgroundColor: '#FF6B35',
    justifyContent: 'center',
    alignItems: 'center',
    ...Platform.select({
      web: {
        boxShadow: '0px 2px 6px rgba(0,0,0,0.16)',
        borderWidth: 0.5,
        borderColor: 'rgba(0,0,0,0.06)',
        zIndex: 2000,
      },
      default: {
        elevation: 2,
        shadowColor: '#000',
        shadowOffset: { width: 0, height: 2 },
        shadowOpacity: 0.16,
        shadowRadius: 6,
        borderWidth: 0.5,
        borderColor: 'rgba(0,0,0,0.06)',
        zIndex: 2000,
      },
    }),
  },
  metaBlockStandalone: { marginTop: 8, borderTopWidth: 1, borderTopColor: '#F3F4F6', paddingTop: 8 },
  metaRowStandalone: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', paddingVertical: 12, borderBottomWidth: 1, borderColor: '#F9FAFB' },
  metaLabelStandalone: { color: '#6B7280', width: '40%' },
  metaValueStandalone: { fontWeight: '700', width: '58%', textAlign: 'right' },
  roundButton: {
    width: 40,
    height: 40,
    borderRadius: 20,
    backgroundColor: '#FF6B35',
    justifyContent: 'center',
    alignItems: 'center',
    ...Platform.select({
      web: {
        boxShadow: '0px 2px 6px rgba(0,0,0,0.16)',
        borderWidth: 0.5,
        borderColor: 'rgba(0,0,0,0.06)',
      },
      default: {
        elevation: 2,
        shadowColor: '#000',
        shadowOffset: { width: 0, height: 2 },
        shadowOpacity: 0.16,
        shadowRadius: 6,
        borderWidth: 0.5,
        borderColor: 'rgba(0,0,0,0.06)',
      },
    }),
  },
  tabsRow: { flexDirection: 'row', alignItems: 'stretch', gap: 8 },
  tabBtn: { paddingVertical: 8, paddingHorizontal: 10, borderRadius: 8 },
  tabBtnActive: { backgroundColor: '#F3F4F6' },
  tabText: { fontSize: 14, color: '#6B7280', fontWeight: '600' },
  tabTextActive: { color: '#111827' },
  tabDivider: { height: 1, backgroundColor: '#E5E7EB', marginTop: 8, marginBottom: 12 },
});

export default CardDetailPage;



// 构造 POI/Activity/Shopping 详情可显示的 meta 列表
function buildPoiMeta(card: any): Array<{ label: string; value: string }> | undefined {
  if (!card) return undefined;
  
  try {
    const src = { ...(card.base_payload || {}), ...card } as any;
    const meta: Array<{ label: string; value: string }> = [];
    
    // First, include metaPairs from base_payload if they exist
    if (card.base_payload && Array.isArray(card.base_payload.metaPairs)) {
      card.base_payload.metaPairs.forEach((pair: any) => {
        if (pair && pair.label && pair.value && pair.value !== '待填写' && pair.value !== '待查詢') {
          meta.push({ label: String(pair.label), value: String(pair.value) });
        }
      });
    }

  // 开放时间
  const opening = src.opening_hours || src.hours || src.open_hours;
  const openingText = formatOpeningHours(opening);
  if (openingText) meta.push({ label: '营业时间', value: openingText });

  // 建议时长（优先 base_payload.recommendedDurationMinutes）
  const minutes = src.recommendedDurationMinutes || src.duration_minutes || src.duration || src.suggested_duration_minutes || src.suggestedDuration;
  if (typeof minutes !== 'undefined' && minutes !== null && String(minutes).trim() !== '') {
    meta.push({ label: '建议时长', value: formatMinutesToHuman(Number(minutes)) });
  }

  // 参考价位 / 门票
  const ticketFree = src.ticketInfo?.isFree === true;
  const price = src.price_range || src.price || src.ticket_price || (ticketFree ? '免费' : undefined) || src.fee;
  if (price) meta.push({ label: '参考价位', value: String(price) });

  // 最佳季节
  if (src.best_season) meta.push({ label: '最佳季节', value: String(src.best_season) });

  // 地点（placeName 优先，否则地址）
  const place = src.placeName || src.address;
  if (place) meta.push({ label: '地点', value: String(place) });

  // 活动类型 / 类别
  const actType = src.type || src.subcategory || src.category;
  if (actType) meta.push({ label: '活动类型', value: String(actType) });

  // 门票（更明确的文案）
  if (typeof src.ticketInfo === 'object') {
    meta.push({ label: '门票', value: ticketFree ? '免费开放' : '请查询' });
  }

  // 提示/小贴士
  if (src.tips) meta.push({ label: '提示', value: String(src.tips) });

  // 联系方式
  const phone = src.phone || src.telephone;
  if (phone) meta.push({ label: '电话', value: String(phone) });

  const website = src.website || src.url || src.link;
  if (website) meta.push({ label: '网站', value: String(website) });

    return meta.length ? meta : undefined;
  } catch (error) {
    console.error('[buildPoiMeta] Error building meta:', error);
    return undefined;
  }
}

function formatMinutesToHuman(mins: number): string {
  if (!Number.isFinite(mins)) return String(mins);
  if (mins >= 60) {
    const h = Math.floor(mins / 60);
    const m = mins % 60;
    return m ? `${h} 小时 ${m} 分钟` : `${h} 小时`;
  }
  if (mins === 0) return '少于 1 分钟';
  return `${mins} 分钟`;
}

function formatOpeningHours(input: any): string | undefined {
  if (!input) return undefined;
  if (typeof input === 'string') return input;
  if (typeof input === 'object') {
    const map = input as Record<string, any>;
    const dayOrder = ['mon','tue','wed','thu','fri','sat','sun'];
    const dayName: Record<string,string> = { mon:'周一', tue:'周二', wed:'周三', thu:'周四', fri:'周五', sat:'周六', sun:'周日' };
    const values = dayOrder.map(d => map[d]).filter(v => typeof v !== 'undefined');
    if (!values.length) return undefined;
    const allSame = values.every(v => v === values[0]);
    if (allSame) {
      const v = String(values[0] ?? '');
      return `周一-周日 ${v || '营业'}`;
    }
    return dayOrder
      .filter(d => typeof map[d] !== 'undefined')
      .map(d => `${dayName[d]} ${map[d]}`)
      .join('；');
  }
  try { return String(input); } catch { return undefined; }
}